import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useCart } from '../../hooks/useCart';

const PRODUCT_IMAGE = 'https://storage.readdy-site.link/project_files/fbdeed4b-ebea-4a2a-94ca-d34ec0e1d4bd/cc708602-b8d9-473a-a5fd-ad64f3c89a7c_herisson-porte-lettre.png?v=8a15e9c968ee51dc9ca42398efc2e833';

const ProduitHerissonPorteLettresLivrePlie = () => {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [showNotification, setShowNotification] = useState(false);

  const product = {
    id: 'herisson-porte-lettres-livre-plie',
    title: 'Hérisson porte-lettres en livre plié (pièce unique)',
    price: 30,
    stock: 1,
    badges: ['Pièce unique'],
    shortDescription: 'Un petit hérisson en livre plié qui garde joliment votre courrier : une création artisanale utile, poétique et pleine de charme.',
  };

  const handleAddToCart = () => {
    addItem({
      id: product.id,
      name: product.title,
      price: product.price,
      image: PRODUCT_IMAGE,
      description: product.shortDescription,
    });
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  const handleBuyNow = () => {
    handleAddToCart();
    setTimeout(() => navigate('/panier'), 300);
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    document.title = 'Hérisson porte-lettres en livre plié – Déco & rangement | Les Carnets de Lolotte';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Hérisson porte-lettres en livre plié, création artisanale unique en livre recyclé. Organiseur courrier poétique et utile – Les Carnets de Lolotte.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'hérisson porte-lettres, livre plié, organiseur courrier, décoration bureau, rangement poétique, pièce unique, upcycling, fait main');
    }

    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', 'Hérisson porte-lettres en livre plié – Déco & rangement | Les Carnets de Lolotte');

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) ogDescription.setAttribute('content', 'Hérisson porte-lettres en livre plié, création artisanale unique en livre recyclé. Organiseur courrier poétique et utile – Les Carnets de Lolotte.');

    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) ogImage.setAttribute('content', PRODUCT_IMAGE);

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', 'https://lescarnetsdelolotte.com/produit-herisson-porte-lettres-livre-plie');

    const jsonLd = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name: product.title,
      image: [PRODUCT_IMAGE],
      description: product.shortDescription,
      brand: {
        '@type': 'Brand',
        name: 'Les Carnets de Lolotte',
      },
      offers: {
        '@type': 'Offer',
        url: 'https://lescarnetsdelolotte.com/produit-herisson-porte-lettres-livre-plie',
        priceCurrency: 'EUR',
        price: product.price,
        availability: 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/NewCondition',
      },
    };

    let scriptTag = document.querySelector('script[type="application/ld+json"]');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.setAttribute('type', 'application/ld+json');
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(jsonLd);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FDF8F5] via-white to-[#FDF8F5]">
      <Header />

      {/* Notification panier */}
      {showNotification && (
        <div className="fixed top-24 right-6 bg-[#B97A72] text-white px-6 py-4 rounded-lg z-50 flex items-center gap-3">
          <div className="w-6 h-6 flex items-center justify-center">
            <i className="ri-check-line text-xl"></i>
          </div>
          <span className="font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>Produit ajouté au panier !</span>
        </div>
      )}

      <main className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">

          {/* Fil d'Ariane */}
          <nav className="mb-8 text-sm text-[#8B7D6C]" style={{ fontFamily: 'Poppins, sans-serif' }}>
            <Link to="/boutique" className="hover:text-[#B97A72] transition-colors">Boutique</Link>
            <span className="mx-2">›</span>
            <span className="text-[#6B5E5A]">Hérisson porte-lettres en livre plié</span>
          </nav>

          <div className="grid lg:grid-cols-2 gap-16 items-start">

            {/* Image principale */}
            <div className="space-y-4">
              <div className="relative bg-[#FAF9F8] rounded-2xl overflow-hidden border border-[#EDD9D4]" style={{ aspectRatio: '1/1' }}>
                <img
                  src={PRODUCT_IMAGE}
                  alt="Hérisson porte-lettres en livre plié – création artisanale fait main"
                  className="w-full h-full object-cover"
                />
                {/* Badge pièce unique */}
                <div className="absolute top-4 left-4 flex flex-col gap-2">
                  <span className="bg-[#C49B86] text-white px-4 py-1.5 rounded-full text-sm font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
                    ✨ Pièce unique
                  </span>
                </div>
              </div>
            </div>

            {/* Informations produit */}
            <div className="space-y-8">

              {/* Titre & description courte */}
              <div>
                <p className="text-sm text-[#B97A72] font-medium mb-2 uppercase tracking-wider" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Décoration poétique &amp; Organiseur
                </p>
                <h1 className="text-4xl lg:text-5xl font-bold text-[#6B5E5A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {product.title}
                </h1>
                <p className="text-lg text-[#8B7D6C] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {product.shortDescription}
                </p>
              </div>

              {/* Prix */}
              <div className="flex items-baseline gap-3">
                <span className="text-5xl font-bold text-[#B97A72]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {product.price} €
                </span>
                <span className="text-sm text-[#8B7D6C]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Frais de livraison calculés au panier
                </span>
              </div>

              {/* Encart stock pièce unique */}
              <div className="bg-[#FDF4F0] border border-[#EDD9D4] rounded-xl p-5 flex items-start gap-4">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-star-fill text-[#C49B86] text-2xl"></i>
                </div>
                <div>
                  <p className="font-semibold text-[#6B5E5A] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Pièce unique faite main — 1 exemplaire disponible
                  </p>
                  <p className="text-sm text-[#8B7D6C]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Chaque création est unique. De légères variations sont normales et font tout le charme de l&apos;artisanat.
                  </p>
                </div>
              </div>

              {/* Sélecteur quantité */}
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <label className="text-[#6B5E5A] font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>Quantité :</label>
                  <div className="flex items-center border border-[#EDD9D4] rounded-lg overflow-hidden bg-white">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                      className="px-4 py-2 text-[#8B7D6C] hover:text-[#B97A72] transition-colors disabled:opacity-40 cursor-pointer"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-subtract-line"></i>
                      </div>
                    </button>
                    <span className="px-4 py-2 text-[#6B5E5A] font-semibold min-w-[2.5rem] text-center" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                      disabled={quantity >= product.stock}
                      className="px-4 py-2 text-[#8B7D6C] hover:text-[#B97A72] transition-colors disabled:opacity-40 cursor-pointer"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-add-line"></i>
                      </div>
                    </button>
                  </div>
                  <span className="text-sm text-[#8B7D6C]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    (1 disponible)
                  </span>
                </div>

                {/* Boutons d'action */}
                <div className="flex gap-3">
                  <button
                    onClick={handleAddToCart}
                    className="flex-1 border-2 border-[#B97A72] text-[#B97A72] px-6 py-3.5 rounded-lg font-medium hover:bg-[#FDF4F0] transition-all duration-200 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-shopping-cart-line"></i>
                    </div>
                    Ajouter au panier
                  </button>
                  <button
                    onClick={handleBuyNow}
                    className="flex-1 bg-[#B97A72] text-white px-6 py-3.5 rounded-lg font-medium hover:bg-[#C49B86] transition-all duration-200 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-flashlight-line"></i>
                    </div>
                    Acheter maintenant
                  </button>
                </div>
              </div>

              {/* Garanties */}
              <div className="border-t border-[#EDD9D4] pt-6 space-y-3">
                <div className="flex items-center gap-3 text-[#8B7D6C]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <div className="w-5 h-5 flex items-center justify-center text-[#B97A72]">
                    <i className="ri-truck-line"></i>
                  </div>
                  <span className="text-sm">Livraison soignée et sécurisée</span>
                </div>
                <div className="flex items-center gap-3 text-[#8B7D6C]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <div className="w-5 h-5 flex items-center justify-center text-[#B97A72]">
                    <i className="ri-gift-line"></i>
                  </div>
                  <span className="text-sm">Emballage soigné, prêt à offrir</span>
                </div>
                <div className="flex items-center gap-3 text-[#8B7D6C]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <div className="w-5 h-5 flex items-center justify-center text-[#B97A72]">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  <span className="text-sm">Création artisanale faite main en France</span>
                </div>
              </div>
            </div>
          </div>

          {/* Description longue */}
          <div className="mt-20 max-w-4xl mx-auto space-y-8">

            <section className="bg-white border border-[#EDD9D4] rounded-2xl p-8">
              <h2 className="text-3xl font-bold text-[#6B5E5A] mb-6 flex items-center gap-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                <div className="w-7 h-7 flex items-center justify-center text-[#B97A72]">
                  <i className="ri-sparkling-line text-2xl"></i>
                </div>
                La création
              </h2>
              <p className="text-[#8B7D6C] text-lg leading-relaxed mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Ce hérisson porte-lettres est une création artisanale réalisée à partir d&apos;un livre recyclé, plié à la main. À la fois décoratif et pratique, il permet de ranger le courrier, des cartes, des petites notes ou des papiers sur une console, dans une entrée ou sur un bureau.
              </p>
              <ul className="space-y-3">
                {[
                  'Pièce unique',
                  'Fait main en France',
                  'Livre recyclé (upcycling)',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-[#8B7D6C]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    <div className="w-5 h-5 flex items-center justify-center text-[#B97A72] flex-shrink-0">
                      <i className="ri-checkbox-circle-fill"></i>
                    </div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="bg-[#FDF4F0] border border-[#EDD9D4] rounded-2xl p-8">
              <h2 className="text-3xl font-bold text-[#6B5E5A] mb-6 flex items-center gap-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                <div className="w-7 h-7 flex items-center justify-center text-[#B97A72]">
                  <i className="ri-ruler-line text-2xl"></i>
                </div>
                Caractéristiques
              </h2>
              <ul className="space-y-4 text-[#8B7D6C]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {[
                  { label: 'Type', value: 'Organiseur / porte-lettres en livre plié' },
                  { label: 'Usage', value: 'Entrée / bureau / rangement courrier' },
                  { label: 'Pièce', value: 'Unique' },
                  { label: 'Fabrication', value: '100% fait main' },
                  { label: 'Stock', value: '1' },
                  { label: 'Dimensions', value: 'à renseigner' },
                ].map((item) => (
                  <li key={item.label} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center text-[#B97A72] flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill"></i>
                    </div>
                    <span><strong className="text-[#6B5E5A]">{item.label} :</strong> {item.value}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="bg-white border border-[#EDD9D4] rounded-2xl p-8">
              <h2 className="text-3xl font-bold text-[#6B5E5A] mb-4 flex items-center gap-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                <div className="w-7 h-7 flex items-center justify-center text-[#B97A72]">
                  <i className="ri-gift-2-line text-2xl"></i>
                </div>
                Idées cadeau
              </h2>
              <p className="text-[#8B7D6C] text-lg leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Idéal en cadeau original : crémaillère, anniversaire, merci, petit bureau. Un objet poétique et utile à la fois, pour les amoureux des livres et de la décoration douce.
              </p>
              <div className="mt-4 flex items-center gap-3 text-[#8B7D6C]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                <div className="w-5 h-5 flex items-center justify-center text-[#B97A72]">
                  <i className="ri-gift-line"></i>
                </div>
                <span className="text-sm">📦 Emballage soigné, prêt à offrir</span>
              </div>
            </section>

          </div>

          {/* CTA retour boutique */}
          <div className="mt-16 text-center">
            <Link
              to="/boutique"
              className="inline-flex items-center gap-2 border-2 border-[#B97A72] text-[#B97A72] px-8 py-3 rounded-full hover:bg-[#FDF4F0] transition-colors font-medium whitespace-nowrap cursor-pointer"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </div>
              Retour à la boutique
            </Link>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProduitHerissonPorteLettresLivrePlie;
